import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  TextField,
  Typography
} from "@mui/material";
import { collection, doc, getDocs, orderBy, query, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { formatDate } from "../../utils/dateUtils";

const statusLabel = (status) => {
  if (status === "handled") return "Traité";
  if (status === "new") return "Nouveau";
  return "Non défini";
};

const Diagnostics = ({ initialSelectedId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");
        const q = query(collection(db, "diagnostic_requests"), orderBy("createdAt", "desc"));
        const snapshot = await getDocs(q);
        const rows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setItems(rows);
      } catch (e) {
        setError("Impossible de charger les diagnostics. " + (e?.message || ""));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!initialSelectedId) return;
    const row = items.find((it) => it.id === initialSelectedId);
    if (row) setSelected(row);
  }, [items, initialSelectedId]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const matchSearch = (row) => {
      if (!needle) return true;
      const hay = [
        row.fullName,
        row.email,
        row.phone,
        row.farmName,
        row.location,
        row.cropType,
        row.stage
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    };
    const matchStatus = (row) => {
      if (statusFilter === "all") return true;
      return (row.status || "unknown") === statusFilter;
    };
    return items.filter((row) => matchStatus(row) && matchSearch(row));
  }, [items, search, statusFilter]);

  const markHandled = async (row) => {
    try {
      setUpdating(true);
      await updateDoc(doc(db, "diagnostic_requests", row.id), {
        status: "handled",
        handledAt: serverTimestamp()
      });
      setItems((prev) => prev.map((it) => (it.id === row.id ? { ...it, status: "handled" } : it)));
      setSelected((prev) => (prev && prev.id === row.id ? { ...prev, status: "handled" } : prev));
    } catch (e) {
      setError("Impossible de mettre à jour le diagnostic. " + (e?.message || ""));
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, color: "#014527", fontWeight: "bold" }}>
        Diagnostics (AVG Consulting)
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, borderRadius: "10px", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)" }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
          <TextField
            label="Rechercher (nom, email, exploitation...)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ minWidth: 280, flex: "1 1 280px" }}
          />
          <TextField
            select
            label="Statut"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="small"
            SelectProps={{ native: true }}
            sx={{ width: 220 }}
          >
            <option value="all">Tous</option>
            <option value="new">Nouveaux</option>
            <option value="handled">Traités</option>
          </TextField>
          <Chip label={`${filtered.length} résultat(s)`} />
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography variant="body1" sx={{ textAlign: "center", py: 3 }}>
            Aucun diagnostic trouvé.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {filtered.map((row, idx) => (
              <Box key={row.id}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    py: 1.5
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                        {row.farmName || "Exploitation non précisée"}
                      </Typography>
                      <Chip
                        size="small"
                        label={statusLabel(row.status)}
                        color={row.status === "handled" ? "success" : row.status === "new" ? "warning" : "default"}
                        variant={row.status === "handled" ? "filled" : "outlined"}
                      />
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {formatDate(row.createdAt)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {row.fullName || "Nom non précisé"} · {row.email || "Email non précisé"}{" "}
                      {row.phone ? `· ${row.phone}` : ""}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {row.location ? `Localisation: ${row.location}` : "Localisation non précisée"}{" "}
                      {row.cropType ? `· Culture: ${row.cropType}` : ""}
                      {row.stage ? `· Stade: ${row.stage}` : ""}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                    <Button variant="outlined" onClick={() => setSelected(row)} sx={{ textTransform: "none" }}>
                      Voir
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      disabled={row.status === "handled" || updating}
                      onClick={() => markHandled(row)}
                      sx={{ textTransform: "none" }}
                    >
                      Marquer traité
                    </Button>
                  </Box>
                </Box>
                {idx < filtered.length - 1 && <Divider />}
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="md">
        <DialogTitle>Détails du diagnostic</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Soumis le: {formatDate(selected.createdAt)}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                {selected.farmName || "Exploitation non précisée"}
              </Typography>
              <Typography variant="body2">
                Statut: <strong>{statusLabel(selected.status)}</strong>
              </Typography>
              <Divider />
              <Typography variant="body2">Nom: {selected.fullName || "Non précisé"}</Typography>
              <Typography variant="body2">Email: {selected.email || "Non précisé"}</Typography>
              <Typography variant="body2">Téléphone: {selected.phone || "Non précisé"}</Typography>
              <Divider />
              <Typography variant="body2">Localisation: {selected.location || "Non précisée"}</Typography>
              <Typography variant="body2">Type de culture: {selected.cropType || "Non précisé"}</Typography>
              <Typography variant="body2">Superficie (ha): {selected.surfaceHa || "Non précisée"}</Typography>
              <Typography variant="body2">Stade du projet: {selected.stage || "Non précisé"}</Typography>
              <Typography variant="body2">Budget estimé: {selected.budget || "Non précisé"}</Typography>
              <Divider />
              <Typography variant="body2" sx={{ fontWeight: 900 }}>
                Besoins
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {(selected.needs || []).length ? (
                  selected.needs.map((n) => <Chip key={n} label={n} size="small" />)
                ) : (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Non précisés
                  </Typography>
                )}
              </Box>
              <Divider />
              <Typography variant="body2" sx={{ fontWeight: 900 }}>
                Message
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                {selected.message || "Aucun message."}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selected && (
            <Button
              variant="contained"
              color="success"
              disabled={selected.status === "handled" || updating}
              onClick={() => markHandled(selected)}
              sx={{ textTransform: "none" }}
            >
              Marquer traité
            </Button>
          )}
          <Button onClick={() => setSelected(null)} sx={{ textTransform: "none" }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Diagnostics;
